import { useEffect } from 'react';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { useOpenDeleteSyncBlockDialog } from 'src/components/unsync';
import { ActivityIds } from 'src/hooks/activities/activity-ids';
import { useUpdateTask } from 'src/hooks/activities/use-update-task';
import { ActivitiesListType } from 'src/hooks/activities/use-activity';
import { useClearSelectedCellValue } from 'src/hooks/block/use-clear-selected-cell-value';
import { useCreatePropertyOption } from 'src/hooks/block/use-create-property-options';
import { useDeleteSelectedTableCells } from 'src/hooks/block/use-delete-selected-table-calls';
import { useUpdatePropertyValueWithoutTransaction } from 'src/hooks/block/use-update-property-value';
import { useUpload } from 'src/hooks/space/use-upload';
import { useTransaction } from 'src/hooks/use-transaction';
import { useFetchPage } from '../use-fetch-page';
import { useSelectedBlockHistory } from '../use-selected-block-history';
import { onCutCopy } from './on-cut-copy';
import { onPaste } from './on-paste';
import { Action } from './types';
import { emitter } from '@flowus/common';
import { isSafari } from 'react-device-detect';

/** 全局 copy 监听 */
export const useCopyListener = (readonly?: boolean) => {
  const transaction = useTransaction();
  const updateTask = useUpdateTask();
  const closeModal = useCloseModal();
  const { selectedBlockHistory } = useSelectedBlockHistory();
  const upload = useUpload();
  const openModal = useOpenModal();
  const clearSelectedCellValue = useClearSelectedCellValue();
  const updatePropertyValueWithoutTransaction = useUpdatePropertyValueWithoutTransaction();
  const createPropertyOption = useCreatePropertyOption();
  const openDeleteSyncBlockDialog = useOpenDeleteSyncBlockDialog();
  const deleteSelectedTableCells = useDeleteSelectedTableCells();
  const fetchPage = useFetchPage();
  const common = {
    transaction,
    selectedBlockHistory,
    clearSelectedCellValue,
    readonly,
    closeModal,
    openDeleteSyncBlockDialog,
    deleteSelectedTableCells,
  };
  const handleCut = onCutCopy(common)(Action.CUT);
  const handleCopy = onCutCopy(common)(Action.COPY);
  const handlePaste = onPaste({
    ...common,
    fetchPage,
    uploadFile: upload,
    openModal,
    createPropertyOption,
    updatePropertyValueWithoutTransaction,
    uploadFileSuccess: () => {
      // 积分任务
      void updateTask(ActivityIds.GUIDE_FIRST_TIME_UPLOAD, ActivitiesListType.basicList);
    },
    pasteSyncBlock: () => {
      // 积分任务
      void updateTask(ActivityIds.CREATE_SYNC_BLOCK_NEW, ActivitiesListType.advancedList, {
        step2: true,
      });
    },
  });

  useEffect(() => {
    let cacheEvent: ClipboardEvent | null = null;
    const clearCacheEvent = () => {
      cacheEvent = null;
    };

    /**
     * 由于safari不支持executeCommand,
     * 因此这里我们自己模拟cut/copy发送event，
     * 拿到event存起来，遇到paste就模拟paste事件发送
     * 这种做法只能支持同一页面的情况，跨页面无解
     */
    const cmd = (command: string) => {
      // 只有safari才会走到这里
      if (command === 'cut' || command === 'copy') {
        cacheEvent = new ClipboardEvent(command, {
          bubbles: true,
          cancelable: true,
          composed: true,
          clipboardData: new DataTransfer(),
        });
        document.dispatchEvent(cacheEvent);
      }
    };
    emitter.on('executeCmd', cmd);
    const paste = (event: ClipboardEvent) => {
      if (isSafari && cacheEvent !== null) {
        handlePaste(cacheEvent);
      } else {
        handlePaste(event);
      }
    };
    const cut = (event: ClipboardEvent) => {
      if (event.isTrusted) {
        clearCacheEvent();
      }
      handleCut(event);
    };
    const copy = (event: ClipboardEvent) => {
      if (event.isTrusted) {
        clearCacheEvent();
      }
      handleCopy(event);
    };
    document.addEventListener('cut', cut, { capture: true });
    document.addEventListener('copy', copy);
    document.addEventListener('paste', paste, { capture: true });

    return () => {
      emitter.off('executeCmd', cmd);
      document.removeEventListener('cut', cut, { capture: true });
      document.removeEventListener('copy', copy);
      document.removeEventListener('paste', paste, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
